import React from 'react';
import ChartDownloadButtons from './ChartDownloadButtons';

interface MetricValue {
  value: number | string;
  label: string;
  goal: number;
  progress: number;
  growth?: number;
  percentage?: number;
  note?: string;
  manual?: boolean;
}

interface AdoptionMetricsData {
  adoption: {
    totalBusinesses: MetricValue;
    payingCustomers: MetricValue;
  };
  usage: {
    totalMessages: MetricValue;
    weeklyInteractions: MetricValue;
    avgSetupTime: MetricValue;
  };
  retention: {
    activeUsersAfter2Weeks: MetricValue;
    timeSaved: MetricValue;
  };
  growth: {
    wowSignups: MetricValue;
    websiteVisits: MetricValue;
    conversionRate: MetricValue;
  };
  monetization: {
    mrr: MetricValue;
    arpu: MetricValue;
  };
  impact: {
    customerReach: MetricValue;
    aiConversationsPerMonth: MetricValue;
  };
  manual: {
    prospectsContacted: MetricValue;
    testimonialsCollected: MetricValue;
    mvpTimeline: MetricValue;
    updatesReleased: MetricValue;
    coldEmailsDemos: MetricValue;
  };
  calculatedAt: string;
}

interface AdoptionMetricsProps {
  data: AdoptionMetricsData | null;
  isLoading: boolean;
}

const MetricCard: React.FC<{ metric: MetricValue }> = ({ metric }) => {
  const getProgressColor = (progress: number) => {
    if (progress >= 70) return 'bg-green-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const progressColor = getProgressColor(metric.progress);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all hover:border-indigo-300 dark:hover:border-indigo-600">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 leading-snug">
          {metric.label}
        </span>
        {metric.manual && (
          <span className="bg-yellow-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide ml-2">
            Manual
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-3xl font-bold text-gray-900 dark:text-white">
          {typeof metric.value === 'number' && metric.value >= 1000
            ? metric.value.toLocaleString()
            : metric.value}
        </span>
        {metric.growth !== undefined && (
          <span
            className={`text-sm font-semibold px-2 py-1 rounded-md ${
              metric.growth >= 0
                ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30'
                : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
            }`}
          >
            {metric.growth > 0 ? '+' : ''}{metric.growth}%
          </span>
        )}
        {metric.percentage !== undefined && (
          <span className="text-base text-gray-600 dark:text-gray-400 font-medium">
            ({metric.percentage}%)
          </span>
        )}
      </div>

      {!metric.manual && (
        <>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full ${progressColor} transition-all duration-500`}
              style={{ width: `${Math.min(metric.progress, 100)}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
            Goal: {typeof metric.goal === 'string' ? metric.goal : metric.goal.toLocaleString()}
          </div>
        </>
      )}

      {metric.note && (
        <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-400 italic bg-gray-100 dark:bg-gray-700/50 px-3 py-2 rounded-md">
          {metric.note}
        </div>
      )}
    </div>
  );
};

const AdoptionMetrics: React.FC<AdoptionMetricsProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-indigo-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading adoption metrics...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
        <svg className="w-12 h-12 text-red-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-700 dark:text-red-300 font-medium">Failed to load adoption metrics</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center pb-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Adoption Metrics Dashboard
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Investor-ready metrics • Last updated: {new Date(data.calculatedAt).toLocaleString()}
        </p>
      </div>

      {/* Chart Download Buttons */}
      <ChartDownloadButtons data={data} />

      {/* Adoption Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-indigo-200 dark:border-indigo-800">
          Adoption Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.adoption.totalBusinesses} />
          <MetricCard metric={data.adoption.payingCustomers} />
        </div>
      </div>

      {/* Usage & Engagement Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-green-200 dark:border-green-800">
          Usage & Engagement
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.usage.totalMessages} />
          <MetricCard metric={data.usage.weeklyInteractions} />
          <MetricCard metric={data.usage.avgSetupTime} />
        </div>
      </div>

      {/* Retention & Satisfaction Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-blue-200 dark:border-blue-800">
          Retention & Satisfaction
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.retention.activeUsersAfter2Weeks} />
          <MetricCard metric={data.retention.timeSaved} />
        </div>
      </div>

      {/* Growth Momentum Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-purple-200 dark:border-purple-800">
          Growth Momentum
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.growth.wowSignups} />
          <MetricCard metric={data.growth.websiteVisits} />
          <MetricCard metric={data.growth.conversionRate} />
        </div>
      </div>

      {/* Early Monetization Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-emerald-200 dark:border-emerald-800">
          Early Monetization
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.monetization.mrr} />
          <MetricCard metric={data.monetization.arpu} />
        </div>
      </div>

      {/* Reach & Impact Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-pink-200 dark:border-pink-800">
          Reach & Impact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.impact.customerReach} />
          <MetricCard metric={data.impact.aiConversationsPerMonth} />
        </div>
      </div>

      {/* Manual Metrics Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-yellow-200 dark:border-yellow-800">
          Founder Velocity (Manual Tracking)
        </h3>
        <div className="bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-500 p-4 mb-5 rounded-r-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            💡 <strong>Note:</strong> Update these metrics manually as you track your outreach and development progress
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <MetricCard metric={data.manual.prospectsContacted} />
          <MetricCard metric={data.manual.testimonialsCollected} />
          <MetricCard metric={data.manual.mvpTimeline} />
          <MetricCard metric={data.manual.updatesReleased} />
          <MetricCard metric={data.manual.coldEmailsDemos} />
        </div>
      </div>
    </div>
  );
};

export default AdoptionMetrics;
